import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { DashboardLayout } from '../layouts';
import { AccentButton } from '../components/Button';
import Modal from '../components/Modal';
import FolderItems from '../views/FolderItems';
import PastedItemView from '../views/PastedItemView';
import Label from '../components/Label';
import BatchActionBar from '../components/BatchActionBar';
import MoveModal from '../components/MoveModal';

import { useItems, useFolders } from '../../hooks/useFolder';
import { useClipboard } from '../../hooks/useClipboard';
import { useSelectionMode } from '../../hooks/useSelectionMode';
import { useBulkActions } from '../../hooks/useBulkActions';

const FolderPage = () => {
  const navigate = useNavigate();
  const { clipboardId } = useParams();

  const { folders } = useFolders();
  const currentFolder = folders.find(folder => folder.id === clipboardId);

  const { items, isLoading, createItem } = useItems(clipboardId);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeMenuItem, setActiveMenuItem] = useState('items');
  const [isPasteOpen, setIsPasteOpen] = useState(false);
  const [pastedItems, setPastedItems] = useState([]);
  const [isMoveOpen, setIsMoveOpen] = useState(false);

  const { isSelecting, selectedIds, enterSelection, exitSelection, toggleItem } = useSelectionMode();
  const { bulkDelete, bulkMove } = useBulkActions();

  const handleBulkDelete = async () => {
    await bulkDelete(selectedIds);
    exitSelection();
  };

  const handleBulkMove = async (targetFolderId) => {
    await bulkMove({ selectedIds, targetFolderId });
    exitSelection();
  };

  const folderOptions = folders.map(folder => ({
    value: folder.id,
    label: folder.name,
    color: 'rgba(74, 186, 98, 1)',
    fill: 'rgba(74, 186, 98, 0.2)',
  }));

  const handleAdd = (item) => {
    setIsPasteOpen(false);
    setPastedItems([]);
    createItem({ blob: item.blob, folderId: item.selectedFolderId });
  };

  const { handlePaste, clipboardRead } = useClipboard((items) => {
    setPastedItems(items);
    setIsPasteOpen(true);
  });

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-[#121212]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#121212] dark:border-[#D0D0D0]"></div>
      </div>
    );
  }

  return (
    <DashboardLayout
      activeMenuItem={activeMenuItem}
      onMenuItemClick={(item) => setActiveMenuItem(item.id)}
      searchQuery={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      onBackClick={() => navigate('/my')}
      onForwardClick={() => {}}
      onProfileClick={() => {}}
    >
      {/* Items Section Header */}
      <div className="border-b border-[#ECECEC] dark:border-[#202020] bg-white dark:bg-[#121212]">
        <div className="h-[70px] flex items-center justify-between px-6">
          <h2 className="text-xl font-medium">
            {currentFolder ? (
              <><Label variant="secondary">Folders</Label> / <Label variant="primary">{currentFolder.name}</Label></>
            ) : (
              <Label variant="primary">All Items</Label>
            )}
          </h2>
          <div className="flex items-center gap-4">
            {isSelecting ? (
              <BatchActionBar
                selectedCount={selectedIds.size}
                onDelete={handleBulkDelete}
                onMove={() => setIsMoveOpen(true)}
                onCancel={exitSelection}
              />
            ) : (
              <>
                <AccentButton variant="ghost" onClick={enterSelection}>Select</AccentButton>
                <AccentButton variant="primary" onClick={() => clipboardRead()}>Paste</AccentButton>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Items Grid */}
      <div className="p-6">
        <FolderItems
          items={items}
          isSelecting={isSelecting}
          selectedIds={selectedIds}
          onToggleSelect={toggleItem}
        />
      </div>

      {/* Paste Content Modal */}
      <Modal isOpen={isPasteOpen} onClose={() => setIsPasteOpen(false)} size="xl">
        <PastedItemView
          pastedItems={pastedItems}
          onAdd={handleAdd}
          onCancel={() => setIsPasteOpen(false)}
          className="w-full h-full"
          folderOptions={folderOptions}
          defaultFolderId={clipboardId}
        />
      </Modal>

      {/* Move Modal */}
      <MoveModal
        isOpen={isMoveOpen}
        onClose={() => setIsMoveOpen(false)}
        folders={folders}
        currentFolderId={clipboardId}
        onConfirm={handleBulkMove}
      />
    </DashboardLayout>
  );
};

export default FolderPage;
